import { Component, inject, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import {
  Meeting,
  MeetingResource,
  MeetingsService,
  MinutesStyling,
  MetricType,
  canUserEdit,
  computeIsCheckingAgendaValidity,
  computeAgendaFailureMessageKey,
  isAgendaExtractionRejectionVisible,
} from '../meetings.service'
import { MeetingDetailsCardComponent, MeetingDetailsSaveData } from '../meeting-details-card/meeting-details-card.component'
import { ProgressLoaderComponent } from '../common/progress-loader/progress-loader.component'
import { MatIcon } from '@angular/material/icon'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { AsyncPipe, DatePipe } from '@angular/common'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatTooltip } from '@angular/material/tooltip'
import { MeetingDocumentUploadComponent, getMissingDocumentsForMeeting } from '../meeting-document-upload/meeting-document-upload.component'
import { UsersService } from '../users.service'
import { ToastService } from '../toast.service'
import { DownloadsService } from '../downloads.service'
import { FormsModule } from '@angular/forms'
import { SelectComponent, SelectOption } from '../common/select/select.component'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MetricsService } from '../metrics.service'
import { MeetingProfilesService } from '../meeting-profiles.service'
import { filter, map } from 'rxjs'
import { MatButton } from '@angular/material/button'

@Component({
  selector: 'app-meeting-info',
  standalone: true,
  imports: [
    MatIcon,
    MinutesStylingComponent,
    DatePipe,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MeetingDocumentUploadComponent,
    MatButton,
    MeetingDetailsCardComponent,
    AsyncPipe,
    FormsModule,
    SelectComponent,
    ProgressLoaderComponent,
  ],
  templateUrl: './meeting-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-info.component.scss',
})
export class MeetingInfoComponent {
  @Input()
  meeting!: Meeting

  @ViewChild(MeetingDetailsCardComponent)
  meetingDetailsCard: MeetingDetailsCardComponent | undefined

  constructor() {}

  resetEditState() {
    if (this.meetingDetailsCard !== undefined) {
      this.meetingDetailsCard.cancelEditDetails()
    }
  }

  meetingsService = inject(MeetingsService)
  usersService = inject(UsersService)
  currentUser$ = this.usersService.currentUser$
  toastService = inject(ToastService)
  downloadsService = inject(DownloadsService)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  meetingProfilesService = inject(MeetingProfilesService)
  dialog = inject(MatDialog)

  enabledMinutesLanguages$ = this.currentUser$.pipe(
    filter((result) => result !== null),
    map((result) => result.team.client.enabledMinutesLanguages)
  )

  updateMeetingProfileMutation = this.meetingsService.updateMeetingProfileMutation()
  meetingProfiles$ = this.meetingProfilesService.getOwnMeetingProfiles()

  patchMeetingMutation = this.meetingsService.patchMutation()
  updateMetadataMutation = this.meetingsService.updateMetadataMutation()
  removeResourceMutation = this.meetingsService.removeResource()
  reprocessOutputsMutation = this.meetingsService.reprocessOutputsMutation()

  get canEdit() {
    return canUserEdit(this.meeting)
  }

  get showReprocessOutputsButton(): boolean {
    return this.meeting.haveMinuteStylesBeenUpdatedSinceLastProcessing
  }

  async onDetailsSaved(saveData: MeetingDetailsSaveData): Promise<void> {
    await this.updateMetadataMutation.mutateAsync({
      meetingId: this.meeting.id,
      data: saveData.metadata,
    })
    if (saveData.dateTime !== null) {
      await this.patchMeetingMutation.mutateAsync({
        meetingId: this.meeting.id,
        data: {
          date: saveData.dateTime.date,
          endDate: saveData.dateTime.endDate,
        },
      })
    }
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  getMeetingProfileOptions(meetingProfiles: { id: number; name: string }[]): SelectOption<number>[] {
    return meetingProfiles.map((profile) => ({ value: profile.id, label: profile.name }))
  }

  async updateMeetingProfile(meetingProfileId: number | null) {
    await this.updateMeetingProfileMutation.mutateAsync({ meetingId: this.meeting.id, data: { meetingProfileId } })
    this.toastService.show('MEETINGS.UPDATED')
  }

  async removeUpload(resource: MeetingResource) {
    await this.removeResourceMutation.mutateAsync({
      meetingId: this.meeting.id,
      resourceType: resource.resourceType,
      index: resource.index,
      extension: resource.extension,
    })
    this.toastService.show('MEETINGS.MEETING_INFO.DOCUMENT_UPLOAD.REMOVE_SUCCESS')
  }

  downloadResource(resource: MeetingResource) {
    if (resource.downloadUrl !== null) {
      const fileName = `${resource.fileName}.${resource.extension}`
      this.downloadsService.downloadFromUrl(resource.downloadUrl, fileName)
    }
  }

  get missingDocuments() {
    const missingDocuments = getMissingDocumentsForMeeting(this.meeting)
    if (this.meeting.agendaType === 'MULTIPLE') {
      return missingDocuments.filter((doc) => doc.type !== 'AGENDA')
    }
    return missingDocuments
  }

  async updateMinutesStyling(styling: Partial<MinutesStyling>) {
    await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: styling })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.meeting.id, MetricType.CHANGE_MINUTING_STYLES, styling)
  }

  get isAuthorizedToManageDocuments() {
    return this.meeting.accessLevel === 'FULL_ACCESS' || this.meeting.accessLevel === 'EDITOR'
  }

  get isCheckingAgendaValidity(): boolean {
    return computeIsCheckingAgendaValidity(this.meeting)
  }

  get isAgendaExtractionFailed(): boolean {
    return isAgendaExtractionRejectionVisible(this.meeting)
  }

  get isInvalidAgendaDocument(): boolean {
    return this.meeting.invalidAgendaReason !== null
  }

  get agendaFailureMessageKey(): string | null {
    return computeAgendaFailureMessageKey(this.meeting.invalidAgendaReason, this.meeting.agendaExtractionFailed)
  }

  get hasAgendaUploaded(): boolean {
    return this.meeting.userUploadedResources.some((resource) => resource.resourceType === 'AGENDA')
  }

  get anyAttendeeMissingHonorific(): boolean {
    return this.meeting.agendaMetadata.attendees.some(
      (attendee) => attendee.honorifics === null || attendee.honorifics === undefined || attendee.honorifics.trim() === ''
    )
  }

  reprocessOutputs() {
    const titleKey = this.meeting.hasMinutes ? 'MEETINGS.REPROCESS_OUTPUTS.TITLE' : 'MEETINGS.REPROCESS_OUTPUTS.TITLE_FIRST_TIME'
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(titleKey),
        description: this.translateService.instant('MEETINGS.REPROCESS_OUTPUTS.DESCRIPTION', { meetingId: this.meeting.id }),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.reprocessOutputsMutation.mutateAsync({ meetingId: this.meeting.id })
      }
    })
  }
}
